import { CountyProjectContextProps, withCountyProjectContext } from "@app/projects/CountyProjectContext";
import { AccuracyProviderDetail, AffiliationAdmittingPrivilegeEnum, BoardCertification, ProviderLicense, PublicTransportation, CulturalCompetencyTraining, MedicaidLicense, AreaOfExpertiseGroupEnum, AppointmentWaitTimeCategory } from "@app/projects/results/accuracy";
import { formatPhoneNumber, formatUnconvertedDateCustom } from "@app/shared";
import { Roles, UserContextProps, withUserContext } from "@app/user";
import { keyBy, merge, sortBy, values } from "lodash";
import * as React from "react";
import { ColumnConfig, Waffles, wafflesKeyColumnName } from "@app/shared/waffles";
import { joinInsurances, populateInsurance } from "./provider-inspection-profile-components/insurances";
import { generateAdditionalKnownSpecialtyProviderPanelRows } from "./provider-inspection-profile-components/additionalKnownSpecialties";
import { generateCredentialProviderPanelRows, getCredentialRowKeys } from "./provider-inspection-profile-components/credentials";
import { generateEntityTypeProviderPanelRow, isOrganizationEntity } from "./provider-inspection-profile-components/entityType";
import { generateLocationConfidenceScoreRow } from "./provider-inspection-profile-components/locationConfidenceScore";
import { generateProviderClaimsActivityRow } from "./provider-inspection-profile-components/providerClaimsActivity";
import { generateProviderTelehealthOriginatingSiteRow, getProviderTelehealthOriginatingSiteRowKeys } from "./provider-inspection-profile-components/providerTelehealthOriginatingSite";
import { generateProviderTelehealthTypeRows, getProviderTelehealthTypeRowKeys } from "./provider-inspection-profile-components/providerTelehealthType";
import { generateSpecialtyProviderPanelRows } from "./provider-inspection-profile-components/specialties";
import populateOfficeHours from "./provider-inspection-profile-components/officeHours";
import { getSupplementalDataDecoration, getSupplementalDataDecorationWithoutSource, valueOrDashes } from "./provider-inspection-profile-components/utils";
import InspectionProfileTableData from "./provider-inspection-profile-components/inspectionProfileTableData";
import generateDefaultProviderPanelRow from "./provider-inspection-profile-components/defaultProviderPanelRow";
import Features from "@app/feature-flags/features";
import { FeatureFlagContextProps, withFeatureFlagContext } from "@app/feature-flags/FeatureFlagContext";
import { QesIcon } from "@app/shared/icons/QESIcon";
import { QesIcons } from "@app/shared/icons/qesIcons";

interface AccuracyProviderInspectionProfileProps {
  provider: AccuracyProviderDetail;
}

const columnConfig: ColumnConfig = {
  reference: {
    displayText: "Reference",
    noSort: true,
    testId: "reference",
  },
  result: {
    displayText: "Result",
    noSort: true,
    testId: "result",
  },
  source: {
    displayText: "Source",
    noSort: true,
    testId: "source",
  },
  title: {
    displayText: "",
    noSort: true,
    testId: "title",
  }
};

class AccuracyProviderInspectionProfile extends React.Component<AccuracyProviderInspectionProfileProps & CountyProjectContextProps & UserContextProps & FeatureFlagContextProps> {
  private tableRowGroups = new Map<string, string[]>();
  private tableRowData : InspectionProfileTableData[] = [];
  constructor(props: AccuracyProviderInspectionProfileProps & CountyProjectContextProps & UserContextProps & FeatureFlagContextProps) {
    super(props);
    this.tableRowData = this.buildTable();
    this.filterTableData();
    this.tableRowGroups = this.getTableRowGroups();
  }

  public render() {
    return (
      <div>
        <div className="card card-fluid mb-0">
          <Waffles<InspectionProfileTableData>
            columns={columnConfig}
            data={this.tableRowData}
            fullWidth={true}
            rowGroups={this.tableRowGroups}
            testId="accuracy-provider-inspection-profile"
          />
        </div>
        {
          <footer
            style={{
              paddingTop: '5px',
            }}>
            <em>
              * This information is proprietary data maintained in a copyrighted database compilation owned by the American Board of Medical Specialties. Copyright {new Date().getFullYear()} American Board of Medical Specialties&reg;. All rights reserved.
            </em>
          </footer>
        }

      </div>
    );
  }

  /* eslint-disable sort-keys */
  private buildTable = () => {
    const record = this.props.provider;
    const unknownSourceName = "UNKNOWN";
    const tableData: InspectionProfileTableData[] = [];

    // General Section - do we want these alphabetical since order of appearance is based on a different array?
    tableData.push(
      generateDefaultProviderPanelRow("npi", "NPI", record.npi, record.inspectionSupplement.npi),
      generateProviderClaimsActivityRow("providerClaimsTotalVolume", "Provider Claims Total Volume", record),
      generateDefaultProviderPanelRow("tin", "TIN", record.tin, record.inspectionSupplement.tin),
      generateEntityTypeProviderPanelRow("entityType", "Entity Type", record),
      generateDefaultProviderPanelRow("locationName", "Location Name", record.organizationName, record.inspectionSupplement.organizationName),
      generateDefaultProviderPanelRow("organizationNpi", "Organization NPI", record.organizationNPI, record.inspectionSupplement.organizationNPI),
      generateDefaultProviderPanelRow("prefix", "Prefix", record.prefix, record.inspectionSupplement.prefix),
      generateDefaultProviderPanelRow("firstName", "First Name", record.firstName, record.inspectionSupplement.firstName),
      generateDefaultProviderPanelRow("middleName", "Middle Name", record.middleName, record.inspectionSupplement.middleName),
      generateDefaultProviderPanelRow("lastName", "Last Name", record.lastName, record.inspectionSupplement.lastName),
      generateDefaultProviderPanelRow("suffix", "Suffix", record.suffix, record.inspectionSupplement.suffix),
      generateDefaultProviderPanelRow("locationPhone", "Location Phone", formatPhoneNumber(record.phone, true), record.inspectionSupplement.phone, formatPhoneNumber(record.inspectionSupplement.phone?.value)),
      generateDefaultProviderPanelRow("locationFax", "Location Fax", formatPhoneNumber(record.fax, true), record.inspectionSupplement.fax, formatPhoneNumber(record.inspectionSupplement.fax?.value)),
      generateDefaultProviderPanelRow("locationEmail", "Location Email", record.organizationEmail, record.inspectionSupplement.organizationEmail),
      generateSpecialtyProviderPanelRows("specialtyCode", "Specialty Code", record),
      generateDefaultProviderPanelRow("gender", "Gender", record.gender, record.inspectionSupplement.gender),
      generateDefaultProviderPanelRow("frequencyAtLocation", "Frequency at Location", record.frequencyAtLocation, record.inspectionSupplement.frequencyAtLocation),
      generateAdditionalKnownSpecialtyProviderPanelRows("additionalKnownSpecialties", "Additional Known Specialties", record.additionalKnownSpecialties),
      generateDefaultProviderPanelRow("providerTelehealthServices", "Provider Telehealth Services", null, record.inspectionSupplement.providerTelehealth)
    );

    generateCredentialProviderPanelRows("credentials", "Credentials", record.credentials, record.inspectionSupplement.credentials).forEach(row => tableData.push(row));
    generateProviderTelehealthTypeRows("providerTelehealthType", "Provider Telehealth Type", record.inspectionSupplement.providerTelehealthChannelOptions).forEach((row) => tableData.push(row));
    generateProviderTelehealthOriginatingSiteRow("providerTelehealthOriginatingSite", "Provider Telehealth Originating Site", record.inspectionSupplement.providerTelehealthOriginatingSites).forEach((row) => tableData.push(row));

    // Address Section
    tableData.push(
      generateDefaultProviderPanelRow("address", "Address", record.address, record.inspectionSupplement.address1),
      generateDefaultProviderPanelRow("address2", "Address 2", record.address2, record.inspectionSupplement.address2),
      generateDefaultProviderPanelRow("city", "City", record.city, record.inspectionSupplement.city),
      generateDefaultProviderPanelRow("state", "State", record.state, record.inspectionSupplement.state),
      generateDefaultProviderPanelRow("zip", "Zip", record.zip, record.inspectionSupplement.zip, formatZipCode(record.inspectionSupplement.zip?.value)),
      generateLocationConfidenceScoreRow("locationConfidenceScore", "Prac. At Location Confidence", record)
    );

    populateInsurance("Accepting New Patients", "acceptingNewPatients", tableData, record.psAcceptingNewPatients, record.inspectionSupplement?.psAcceptingNewPatients);
    populateInsurance("Not Accepting New Patients", "notAcceptingNewPatients", tableData, record.psNotAcceptingPatients, record.inspectionSupplement?.psNotAcceptingPatients);
    populateInsurance("Accepting Patients With Limitations", "acceptingPatientsWithLimitations", tableData, record.psAcceptingNewPatientsWithLimitations, record.inspectionSupplement?.psAcceptingNewPatientsWithLimitations, true);
    populateInsurance("Accepting Existing Patients Only", "acceptingExistingPatientsOnly", tableData, record.psAcceptingExistingPatientsOnly, record.inspectionSupplement?.psAcceptingExistingPatientsOnly);

    tableData.push(
      {
        [wafflesKeyColumnName]: "adaFeatures",
        title: "ADA Features",
        source: valueOrDashes((record.adaFeatures || "").toString().replace(/_/g, " ")),
        result: (
          record.inspectionSupplement.adaFeatures ?
            getSupplementalDataDecoration((record.inspectionSupplement.adaFeatures.value || "").toString().replace(/_/g, " "), record.inspectionSupplement.adaFeatures.supplementalType, record.adaFeatures)
            :
            <span className="text-muted">
              <QesIcon iconName={QesIcons.FontAwesome.Question} width="8" description="the accuracy of this information is unknown" classNames="mx-2" />
            </span>
        ),
        reference: (
          (record.inspectionSupplement.adaFeatures && record.inspectionSupplement.adaFeatures.sourceName.toUpperCase() !== unknownSourceName) ?
            <span>
              {valueOrDashes(record.inspectionSupplement.adaFeatures ? record.inspectionSupplement.adaFeatures.sourceName : "")}
              &nbsp;
              {(record.inspectionSupplement.adaFeatures && record.inspectionSupplement.adaFeatures.sourceName) ?
                valueOrDashes(record.inspectionSupplement.adaFeatures.sourceDate ? `(${formatUnconvertedDateCustom(record.inspectionSupplement.adaFeatures.sourceDate, "L")})` : "") :
                null
              }
            </span> :
            <span>{"---"}</span>
        )
      }
    )

    if (record.providerLanguages?.length || record.inspectionSupplement.providerLanguages?.length) {
      joinProviderLanguages(record).forEach((providerLanguage, index) => {
        tableData.push(
          {
            [wafflesKeyColumnName]: `providerLanguages-${index}`,
            title: index === 0 ? "Provider Languages" : "",
            source: (
              providerLanguage.name ?
                <div key={index}>
                  <div>
                    {valueOrDashes(providerLanguage.name)}
                  </div>
                </div> :
                ""
            ),
            result: (
              <div key={index}>
                {getSupplementalDataDecoration(
                  (<span>{valueOrDashes(providerLanguage.value)}</span>),
                  providerLanguage.supplementalType,
                  null,
                )}
              </div>
            ),
            reference: (
              (record.inspectionSupplement.providerLanguages && record.inspectionSupplement.providerLanguages[0]?.sourceName.toUpperCase() !== unknownSourceName) ?
                <span>
                  {valueOrDashes(providerLanguage.sourceName)}
                  &nbsp;
                  {(providerLanguage.sourceName) ?
                    valueOrDashes(providerLanguage.sourceDate ? `(${formatUnconvertedDateCustom(providerLanguage.sourceDate, "L")})` : "") :
                    null
                  }
                </span> :
                <span>{"---"}</span>)
          }
        )
      })
    } else {
      tableData.push(
        generateDefaultProviderPanelRow("providerLanguages", "Provider Languages", null, null)
      );
    }

    if (record.locationLanguages?.length || record.inspectionSupplement.locationLanguages?.length) {
      joinLocationLanguages(record).forEach((locationLanguage, index) => {
        tableData.push(
          {
            [wafflesKeyColumnName]: `locationLanguages-${index}`,
            title: index === 0 ? "Location Languages" : "",
            source: (
              locationLanguage.name ?
                <div key={index}>
                  <div>
                    {valueOrDashes(locationLanguage.name)}
                  </div>
                </div> :
                ""
            ),
            result: (
              <div key={index}>
                {getSupplementalDataDecoration(
                  (<span>{valueOrDashes(locationLanguage.value)}</span>),
                  locationLanguage.supplementalType,
                  null,
                )}
              </div>
            ),
            reference: (
              (record.inspectionSupplement.locationLanguages && record.inspectionSupplement.locationLanguages[0]?.sourceName.toUpperCase() !== unknownSourceName) ?
                <span>
                  {valueOrDashes(locationLanguage.sourceName)}
                  &nbsp;
                  {(locationLanguage.sourceName) ?
                    valueOrDashes(locationLanguage.sourceDate ? `(${formatUnconvertedDateCustom(locationLanguage.sourceDate, "L")})` : "") :
                    null
                  }
            </span>:
                <span>{"---"}</span>)
          }
        )
      })
    } else {
      tableData.push(
        generateDefaultProviderPanelRow("locationLanguages", "Location Languages", null, null)
      );
    }

    tableData.push(
      {
        [wafflesKeyColumnName]: "organizationWebsite",
        title: "Organization Website",
        source: (
          <span className="servicing-provider-profile-website">
            {valueOrDashes(record.organizationWebsite)}
          </span>
        ),
        result: (
          record.inspectionSupplement.organizationWebsite ?
            <span className="servicing-provider-profile-website">
              {getSupplementalDataDecoration(record.inspectionSupplement.organizationWebsite.value, record.inspectionSupplement.organizationWebsite.supplementalType, record.organizationWebsite)}
            </span>
            :
            <span className="text-muted">
              <QesIcon iconName={QesIcons.FontAwesome.Question} width="8" description="the accuracy of this information is unknown" classNames="mx-2" />
            </span>
        ),
        reference: (
          (record.inspectionSupplement.organizationWebsite && record.inspectionSupplement.organizationWebsite.sourceName.toUpperCase() !== unknownSourceName) ?
            <span>
              {valueOrDashes(record.inspectionSupplement.organizationWebsite ? record.inspectionSupplement.organizationWebsite.sourceName : "")}
              &nbsp;
              {(record.inspectionSupplement.organizationWebsite && record.inspectionSupplement.organizationWebsite.sourceName)
                ? valueOrDashes(record.inspectionSupplement.organizationWebsite.sourceDate ? `(${formatUnconvertedDateCustom(record.inspectionSupplement.organizationWebsite.sourceDate, "L")})` : "")
                : null
              }
            </span> :
            <span>{"---"}</span>)
      },

    )

    populateOfficeHours(record.officeHoursViewModels, tableData);

    if (record.groupAffiliation?.length || record.inspectionSupplement.groupAffiliation?.length) {
      joinGroupAffiliation(record).map((affil, index) => {
        tableData.push(
          {
            [wafflesKeyColumnName]: `groupAffiliation-${index}`,
            title: index === 0 ? "Group Affiliation" : "",
            source: (
              affil.name ?
                <div key={index}>
                  <div>
                    {valueOrDashes(affil.name)}
                  </div>
                </div> :
                ""
            ),
            result: (
              affil && affil.value && affil.value.name ?
              <div key={index}>
                {getSupplementalDataDecoration(
                  (<span>{valueOrDashes(affil.value.name)}</span>),
                  affil.supplementalType,
                  null,
                )}
              </div> :
              <div>
                {getSupplementalDataDecoration(null, null, null)}
              </div>
            ),
            reference: (
              (record.inspectionSupplement.groupAffiliation && record.inspectionSupplement.groupAffiliation[0]?.sourceName.toUpperCase() !== unknownSourceName) ?
                <span>
                  {valueOrDashes(affil.sourceName)}
                  &nbsp;
                  {(affil.sourceName) ?
                    valueOrDashes(affil.sourceDate ? `(${formatUnconvertedDateCustom(affil.sourceDate, "L")})` : "") :
                    null
                  }
            </span>:
                <span>{"---"}</span>)
          }
        )
      })
    } else {
      tableData.push(
        generateDefaultProviderPanelRow("groupAffiliation", "Group Affiliation", null, null)
      )
    }

    if (record.hospitalAffiliation?.length || record.inspectionSupplement.hospitalAffiliation?.length) {
      joinHospitalAffiliation(record).forEach((affil, index) => {
        tableData.push(
          {
            [wafflesKeyColumnName]: `hospitalAffiliation-${index}`,
            title: index === 0 ? "Hospital Affiliation" : "",
            source: (
              affil && affil.name ?
                <div key={index}>
                  <div>
                    {valueOrDashes(affil.name)}: {valueOrDashes(affil.admittingPrivilege && affil.admittingPrivilege === AffiliationAdmittingPrivilegeEnum.None ? null : affil.admittingPrivilege)}
                  </div>
                </div> :
                ""
            ),
            result: (
              affil && affil.value && affil.value.name ?
                <div key={index}>
                  {getSupplementalDataDecoration(
                    (<span>{valueOrDashes(affil.value.name)}: {valueOrDashes(affil.value.admittingPrivilege && affil.value.admittingPrivilege === AffiliationAdmittingPrivilegeEnum.None ? null : affil.value.admittingPrivilege)}</span>),
                    affil.supplementalType,
                    null,
                  )}
                </div> :
                <div>
                  {getSupplementalDataDecoration(null, null, null)}
                </div>
            ),
            reference: (
              (record.inspectionSupplement.hospitalAffiliation && record.inspectionSupplement.hospitalAffiliation[0]?.sourceName.toUpperCase() !== unknownSourceName) ?
                <span>
                  {valueOrDashes(affil.sourceName)}
                  &nbsp;
                  {(affil.sourceName) ?
                    valueOrDashes(affil.sourceDate ? `(${formatUnconvertedDateCustom(affil.sourceDate, "L")})` : "") :
                    null
                  }
            </span>:
                <span>{"---"}</span>)
          }
        );
      })
    } else {
      tableData.push(
        generateDefaultProviderPanelRow("hospitalAffiliation", "Hospital Affiliation", null, null)
      )
    }

    if (record.ipaAffiliation?.length || record.inspectionSupplement.ipaAffiliation?.length) {
      joinIPAAffiliation(record).forEach((affil, index) => {
        tableData.push(
          {
            [wafflesKeyColumnName]: `ipaAffiliation-${index}`,
            title: index === 0 ? "IPA Affiliation" : "",
            source: (
              affil.name ?
                <div key={index}>
                  <div>
                    {valueOrDashes(affil.name)}
                  </div>
                </div> :
                ""
            ),
            result: (
              affil && affil.value && affil.value.name ?
              <div key={index}>
                {getSupplementalDataDecoration(
                  (<span>{valueOrDashes(affil.value.name)}</span>),
                  affil.supplementalType,
                  null,
                )}
              </div> :
              <div>
                {getSupplementalDataDecoration(null, null, null)}
              </div>
            ),
            reference: (
              (record.inspectionSupplement.ipaAffiliation && record.inspectionSupplement.ipaAffiliation[0]?.sourceName.toUpperCase() !== unknownSourceName) ?
                <span>
                  {valueOrDashes(affil.sourceName)}
                  &nbsp;
                  {(affil.sourceName) ?
                    valueOrDashes(affil.sourceDate ? `(${formatUnconvertedDateCustom(affil.sourceDate, "L")})` : "") :
                    null
                  }
                </span> :
                <span>{"---"}</span>)
          }
        );
      })
    } else {
      tableData.push(
        generateDefaultProviderPanelRow("ipaAffiliation", "IPA Affiliation", null, null)
      )
    }

    if (record.inspectionSupplement.boardCertifications?.length) {
      record.inspectionSupplement.boardCertifications.forEach((boardCertification, index) => {
        tableData.push(
          {
            [wafflesKeyColumnName]: `boardCertifications-${index}`,
            title: index === 0 ? "Board Certifications*" : "",
            source: "",
            result: (
              getSupplementalDataDecoration(
                (<span>{valueOrDashes(generateBoardCertificationName(boardCertification.value))}</span>),
                boardCertification.supplementalType,
                null,
              )
            ),
            reference: (
              (record.inspectionSupplement.boardCertifications && record.inspectionSupplement.boardCertifications[0]?.sourceName.toUpperCase() !== unknownSourceName) ?
                <span>
                  {valueOrDashes(boardCertification.sourceName)}
                  &nbsp;
                  {(boardCertification.sourceName) ?
                    valueOrDashes(boardCertification.sourceDate ? `(${formatUnconvertedDateCustom(boardCertification.sourceDate, "L")})` : "") :
                    null
                  }
                </span> :
                <span>{"---"}</span>)
          }
        )
      })
    } else {
      tableData.push(
        generateDefaultProviderPanelRow("boardCertifications", "Board Certifications*", null, null)
      )
    }

    if (record.inspectionSupplement.medicaidLicenses?.length) {
      record.inspectionSupplement.medicaidLicenses.forEach((medicaidLicense, index) => {
        tableData.push(
          {
            [wafflesKeyColumnName]: `medicaidLicenses-${index}`,
            title: index === 0 ? "Medicaid License" : "",
            source: "",
            result: (
              <div key={index}>
                {getSupplementalDataDecoration(
                  (<span>{valueOrDashes(joinMedicaidLicense(medicaidLicense.value))}</span>),
                  medicaidLicense.supplementalType,
                  null,
                )}
              </div>
            ),
            reference: (
              (record.inspectionSupplement.medicaidLicenses && record.inspectionSupplement.medicaidLicenses[0]?.sourceName.toUpperCase() !== unknownSourceName) ?
                <span>
                  {valueOrDashes(medicaidLicense.sourceName)}
                  &nbsp;
                  {(medicaidLicense.sourceName) ?
                    valueOrDashes(medicaidLicense.sourceDate ? `(${formatUnconvertedDateCustom(medicaidLicense.sourceDate, "L")})` : "") :
                    null
                  }
                </span> :
                <span>{"---"}</span>)
          }
        );
      })
    } else {
      tableData.push(
        generateDefaultProviderPanelRow("medicaidLicenses", "Medicaid Licenses", null, null)
      )
    }

    if (record.inspectionSupplement.race?.length) {
      record.inspectionSupplement.race.forEach((race, index) => {
        tableData.push(
          {
            [wafflesKeyColumnName]: `race-${index}`,
            title: index === 0 ? "Race" : "",
            source: "",
            result: (
              <div key={index}>
                {getSupplementalDataDecoration(
                  (<span>{valueOrDashes(race.value)}</span>),
                  race.supplementalType,
                  null,
                )}
              </div>
            ),
            reference: (
              (record.inspectionSupplement.race && record.inspectionSupplement.race[0]?.sourceName.toUpperCase() !== unknownSourceName) ?
                <span>
                  {valueOrDashes(race.sourceName)}
                  &nbsp;
                  {(race.sourceName) ?
                    valueOrDashes(race.sourceDate ? `(${formatUnconvertedDateCustom(race.sourceDate, "L")})` : "") :
                    null
                  }
                </span> :
                <span>{"---"}</span>)
          }
        );
      })
    } else {
      tableData.push(
        generateDefaultProviderPanelRow("race", "Race", null, null)
      )
    }

    if (record.inspectionSupplement.ethnicity?.length) {
      record.inspectionSupplement.ethnicity.forEach((ethnicity, index) => {
        tableData.push(
          {
            [wafflesKeyColumnName]: `ethnicity-${index}`,
            title: index === 0 ? "Ethnicity" : "",
            source: "",
            result: (
              <div key={index}>
                {getSupplementalDataDecoration(
                  (<span>{valueOrDashes(ethnicity.value)}</span>),
                  ethnicity.supplementalType,
                  null,
                )}
              </div>
            ),
            reference: (
              (record.inspectionSupplement.ethnicity && record.inspectionSupplement.ethnicity[0]?.sourceName.toUpperCase() !== unknownSourceName) ?
                <span>
                  {valueOrDashes(ethnicity.sourceName)}
                  &nbsp;
                  {(ethnicity.sourceName) ?
                    valueOrDashes(ethnicity.sourceDate ? `(${formatUnconvertedDateCustom(ethnicity.sourceDate, "L")})` : "") :
                    null
                  }
                </span> :
                <span>{"---"}</span>)
          }
        );
      })
    } else {
      tableData.push(
        generateDefaultProviderPanelRow("ethnicity", "Ethnicity", null, null)
      )
    }

    if (record.providerLicenses?.length || record.inspectionSupplement.providerLicenses?.length) {
      joinProviderLicenses(record).forEach((joined, index) => {
        tableData.push(
          {
            [wafflesKeyColumnName]: `providerLicenses-${index}`,
            title: index === 0 ? "Provider Licenses" : "",
            source: (
              joined && joined.license ?
                <div key={index}>
                  <div>
                    {valueOrDashes(joined.license.name)}
                  </div>
                </div> :
                ""
            ),
            result: (
              joined && joined.supplement ?
                <div key={index}>
                  {getSupplementalDataDecoration(
                    (<span>{valueOrDashes(joined.supplement.name)}</span>),
                    joined.supplement.value.supplementalType,
                    null,
                  )}
                </div> :
                ""
            ),
            reference: (
              joined && joined.supplement && joined.supplement.value ?
                (record.inspectionSupplement.providerLicenses && record.inspectionSupplement.providerLicenses[0]?.sourceName.toUpperCase() !== unknownSourceName) ?
                  <div>
                    {valueOrDashes(joined.supplement.value.sourceName)}
                    &nbsp;
                    {(joined.supplement.value.sourceDate) ?
                      valueOrDashes(joined.supplement.value.sourceDate ? `(${formatUnconvertedDateCustom(joined.supplement.value.sourceDate, "L")})` : "") :
                      null
                    }
                </div>:
                  <div>{"---"}</div>
                : <>{valueOrDashes(null)}</>
            )
          }
        )
      })
    } else {
      tableData.push(
        generateDefaultProviderPanelRow("providerLicenses", "Provider Licenses", null, null)
      )
    }

    tableData.push(
      {
        [wafflesKeyColumnName]: "locationTelehealthServices",
        title: "Location Telehealth Services",
        source: "---",
        result: (
          record.inspectionSupplement.locationTelehealth ?
            getSupplementalDataDecoration(record.inspectionSupplement.locationTelehealth.value, record.inspectionSupplement.locationTelehealth.supplementalType, record.locationTelehealth)
            :
            <span className="text-muted">
              <QesIcon iconName={QesIcons.FontAwesome.Question} width="8" description="the accuracy of this information is unknown" classNames="mx-2" />
            </span>
        ),
        reference: (
          (record.inspectionSupplement.locationTelehealth && record.inspectionSupplement.locationTelehealth.sourceName.toUpperCase() !== unknownSourceName) ?
            <span>
              {valueOrDashes(record.inspectionSupplement.locationTelehealth ? record.inspectionSupplement.locationTelehealth.sourceName : "")}
              &nbsp;
              {(record.inspectionSupplement.locationTelehealth && record.inspectionSupplement.locationTelehealth.sourceName) ?
                valueOrDashes(record.inspectionSupplement.locationTelehealth.sourceDate ? `(${formatUnconvertedDateCustom(record.inspectionSupplement.locationTelehealth.sourceDate, "L")})` : "") :
                null
              }
            </span> :
            <span>{"---"}</span>)
      }
    )

    if (!this.props.enabledFeatureFlags.has(Features.QES_ACCURACY_ENABLE_LOCATION_WAIT_TIMES_PHASE_TWO)) {
      // if the phase 2 wait times FF is off and the inspector outcome is in the new supplement, then place the new styled supplement into the old location
      if (record.inspectionSupplement.locationAppointmentWaitTimes.length > 0) {
        const appointmentWaitTime = record.inspectionSupplement.locationAppointmentWaitTimes[0];
        tableData.push(
          {
            [wafflesKeyColumnName]: "locationAppointmentWaitTime",
            title: "Location Appointment Wait Time",
            source: "---",
            result: (
              appointmentWaitTime
                ? getSupplementalDataDecoration(<span>{valueOrDashes(getFriendlyAppointmentWaitTimeName(appointmentWaitTime.value.value))}</span>, appointmentWaitTime.supplementalType, null)
                : <span className="text-muted">
                    <QesIcon iconName={QesIcons.FontAwesome.Question} width="8" description="the accuracy of this information is unknown" classNames="mx-2" />
                  </span>
            ),
            reference: (
              (appointmentWaitTime && appointmentWaitTime.sourceName.toUpperCase() !== unknownSourceName) ?
                <span>
                  {valueOrDashes(appointmentWaitTime ? appointmentWaitTime.sourceName : "")}
                  &nbsp;
                  {(appointmentWaitTime && appointmentWaitTime.sourceName) ?
                    valueOrDashes(appointmentWaitTime.sourceDate ? `(${formatUnconvertedDateCustom(appointmentWaitTime.sourceDate, "L")})` : "") :
                    null
                  }
                </span> :
                <span>{"---"}</span>)
          }
        )
      } else {
        // this is for if the phase 2 FF is off and the project run is using the legacy supplement
        tableData.push(
          generateDefaultProviderPanelRow("locationAppointmentWaitTime", "Location Appointment Wait Time", null, record.inspectionSupplement.locationAppointmentWaitTime)
        )
      }
    }

    if (this.props.enabledFeatureFlags.has(Features.ACCURACY_ENABLE_LOCATION_CERTIFIED_SKILLED_MEDICAL_INTERPRETER)) {
      if (record.inspectionSupplement.locationCertifiedSkilledMedicalInterpreter != null)
        record.inspectionSupplement.locationCertifiedSkilledMedicalInterpreter.value = getFriendlyYesNoUnknownLabel(record.inspectionSupplement.locationCertifiedSkilledMedicalInterpreter.value?.toLowerCase());
      tableData.push(
        generateDefaultProviderPanelRow("locationCertifiedSkilledMedicalInterpreter", "Location Certified Skilled Medical Interpreter", null, record.inspectionSupplement.locationCertifiedSkilledMedicalInterpreter)
      )
    }

    if (this.props.enabledFeatureFlags.has(Features.ACCURACY_ENABLE_LOCATION_GENDER_AFFIRMING_CARE)) {
      if (record.inspectionSupplement.locationGenderAffirmingCare != null)
        record.inspectionSupplement.locationGenderAffirmingCare.value = getFriendlyYesNoUnknownLabel(record.inspectionSupplement.locationGenderAffirmingCare.value?.toLowerCase());
      tableData.push(
        generateDefaultProviderPanelRow("locationGenderAffirmingCare", "Location Gender Affirming Care", null, record.inspectionSupplement.locationGenderAffirmingCare)
      )
    }

    if (record.inspectionSupplement.publicTransportations?.length) {
      record.inspectionSupplement.publicTransportations.forEach((publicTransportation, index) => {
        tableData.push(
          {
            [wafflesKeyColumnName]: `publicTransportation-${index}`,
            title: index === 0 ? "Public Transportation" : "",
            source: "",
            result: (
              <div key={index}>
                {getSupplementalDataDecoration(
                  (<span>{valueOrDashes(joinPublicTransportation(publicTransportation.value))}</span>),
                  publicTransportation.supplementalType,
                  null,
                )}
              </div>
            ),
            reference: (
              (record.inspectionSupplement.publicTransportations && record.inspectionSupplement.publicTransportations[0]?.sourceName.toUpperCase() !== unknownSourceName) ?
                <span>
                  {valueOrDashes(publicTransportation.sourceName)}
                  &nbsp;
                  {(publicTransportation.sourceName) ?
                    valueOrDashes(publicTransportation.sourceDate ? `(${formatUnconvertedDateCustom(publicTransportation.sourceDate, "L")})` : "") :
                    null
                  }
                </span> :
                <span>{"---"}</span>),
          }
        )
      })
    } else {
      tableData.push(
        generateDefaultProviderPanelRow("publicTransportation", "Public Transportation", null, null)
      )
    }

    if (record.inspectionSupplement.culturalCompetencyTrainings?.length) {
      record.inspectionSupplement.culturalCompetencyTrainings.forEach((culturalCompetencyTraining, index) => {
        tableData.push(
          {
            [wafflesKeyColumnName]: `culturalCompetencyTraining-${index}`,
            title: index === 0 ? "Cultural Competency Training" : "",
            source: "",
            result: (
              <div key={index}>
                {getSupplementalDataDecoration(
                  (<span>{valueOrDashes(joinCulturalCompetencyTraining(culturalCompetencyTraining.value))}</span>),
                  culturalCompetencyTraining.supplementalType,
                  null,
                )}
              </div>
            ),
            reference: (
              (record.inspectionSupplement.culturalCompetencyTrainings && record.inspectionSupplement.culturalCompetencyTrainings[0]?.sourceName.toUpperCase() !== unknownSourceName) ?
                <span>
                  {valueOrDashes(culturalCompetencyTraining.sourceName)}
                  &nbsp;
                  {(culturalCompetencyTraining.sourceName) ?
                    valueOrDashes(culturalCompetencyTraining.sourceDate ? `(${formatUnconvertedDateCustom(culturalCompetencyTraining.sourceDate, "L")})` : "") :
                    null
                  }
                </span> :
                <span>{"---"}</span>),
          }
        )
      })
    } else {
      tableData.push(
        generateDefaultProviderPanelRow("culturalCompetencyTraining", "Cultural Competency Training", null, null)
      )
    }

    if (record.inspectionSupplement.taxonomy?.length) {
      record.inspectionSupplement.taxonomy.forEach((taxonomy, index) => {
        tableData.push(
          {
            [wafflesKeyColumnName]: `taxonomy-${index}`,
            title: index === 0 ? "Taxonomy" : "",
            source: "",
            result: (
              <div key={index}>
                {getSupplementalDataDecoration(
                  (<span>{valueOrDashes(taxonomy.value)}</span>),
                  taxonomy.supplementalType,
                  null,
                )}
              </div>
            ),
            reference: (
              (record.inspectionSupplement.taxonomy && record.inspectionSupplement.taxonomy[0]?.sourceName.toUpperCase() !== unknownSourceName) ?
                <span>
                  {valueOrDashes(taxonomy.sourceName)}
                  &nbsp;
                  {(taxonomy.sourceName) ?
                    valueOrDashes(taxonomy.sourceDate ? `(${formatUnconvertedDateCustom(taxonomy.sourceDate, "L")})` : "") :
                    null
                  }
                </span> :
                <span>{"---"}</span>),
          }
        )
      })
    } else {
      tableData.push(
        generateDefaultProviderPanelRow("taxonomy", "Taxonomy", null, null)
      )
    }

    if (this.props.enabledFeatureFlags.has(Features.QES_ACCURACY_ENABLE_LOCATION_WAIT_TIMES_PHASE_TWO)) {
      // this is for if the phase 2 FF is on and the project run is using the legacy supplement
      if (record.inspectionSupplement.locationAppointmentWaitTime) {
        tableData.push(
          generateDefaultProviderPanelRow("preventiveCareAppointment", AppointmentWaitTimeCategory.Preventive, null, null),
          generateDefaultProviderPanelRow("routineDiagnosticCareAppointment", AppointmentWaitTimeCategory.RoutineDiagnostic, null, record.inspectionSupplement.locationAppointmentWaitTime),
          generateDefaultProviderPanelRow("urgentCareAppointment", AppointmentWaitTimeCategory.Urgent, null, null),
          generateDefaultProviderPanelRow("specialtyCareAppointment", AppointmentWaitTimeCategory.Specialty, null, null)
        )
      }
      else {
        // if the phase 2 wait times FF is on and the inspector outcome is in the new supplement, then place the new styled supplement into the new location
        tableData.push(
          this.getAppointmentWaitTimesTableData(AppointmentWaitTimeCategory.Preventive, record, "preventiveCareAppointment"),
          this.getAppointmentWaitTimesTableData(AppointmentWaitTimeCategory.RoutineDiagnostic, record, "routineDiagnosticCareAppointment"),
          this.getAppointmentWaitTimesTableData(AppointmentWaitTimeCategory.Urgent, record, "urgentCareAppointment"),
          this.getAppointmentWaitTimesTableData(AppointmentWaitTimeCategory.Specialty, record, "specialtyCareAppointment")
        )
      }
    }

    tableData.push(
      this.getAreaOfExpertiseTableData(AreaOfExpertiseGroupEnum.Conditions, record, "conditionsTreated"),
      this.getAreaOfExpertiseTableData(AreaOfExpertiseGroupEnum.Procedures, record, "proceduresAndTreatmentsPerformed"),
      this.getAreaOfExpertiseTableData(AreaOfExpertiseGroupEnum.PopulationByAge, record, "populationsTreatedAge"),
      this.getAreaOfExpertiseTableData(AreaOfExpertiseGroupEnum.PopulationByGender, record, "populationsTreatedGender"),
      this.getAreaOfExpertiseTableData(AreaOfExpertiseGroupEnum.PopulationByGroup, record, "populationsTreatedGroup"),
    )

    return tableData;
  }

  private filterTableData = () => {

    // Filter TIN
    if (!this.props.user.roles.includes(Roles.TIN_VIEWER)) { this.removeFromTableData("tin"); }

    // Filter PCA
    if (this.props.runConfiguration.excludeProviderClaimsActivityInspector || !this.props.enabledFeatureFlags.has(Features.ACCURACY_ENABLE_PROVIDER_CLAIMS_ACTIVITY)) {
      this.removeFromTableData("providerClaimsTotalVolume");
    }

    // Filter LCS
    if (isOrganizationEntity || this.props.runConfiguration.excludeAccuracyPractitionerAtLocationInspector || !this.props.enabledFeatureFlags.has(Features.ACCURACY_ENABLE_PRAC_AT_LOC_CONFIDENCE)) {
      this.removeFromTableData("locationConfidenceScore");
    }

    // Filter Telehealth Options
    if (!this.props.enabledFeatureFlags.has(Features.ACCURACY_ENABLE_PROVIDER_TELEHEALTH_INSPECTOR_UPDATE)) {
      this.removeCollectionFromTableData(getProviderTelehealthTypeRowKeys());
      this.removeCollectionFromTableData(getProviderTelehealthOriginatingSiteRowKeys());
    }
  }

  private removeCollectionFromTableData = (rowKeys: string[]): void => {
    const indexesToRemove : number[] = [];
    rowKeys.forEach((value: string) => {
        indexesToRemove.push(this.tableRowData.findIndex((row) => row.key === value));
    });
    // must reverse because indexes change while removing items
    indexesToRemove.reverse().forEach((index) => {
      this.tableRowData.splice(index, 1);
    });
  }

  private removeFromTableData = (key: string): void => {
    this.tableRowData.filter( (value: InspectionProfileTableData, index: number, array: InspectionProfileTableData[]) => {
        if (value.key === key) {
          array.splice(index, 1);
          return true;
        }
        return false;
      });
  }

  private getTableRowGroups = () => {
    const record = this.props.provider;
    const credentialsKeys = getCredentialRowKeys() ?? ["credentials"];
    const providerTelehealthTypeKeys = getProviderTelehealthTypeRowKeys() ?? ["providerTelehealthType"];
    const providerTelehealthOriginatingSiteKeys = getProviderTelehealthOriginatingSiteRowKeys() ?? ["providerTelehealthOriginatingSite"];

    const acceptingNewPatientsKeys = record.psAcceptingNewPatients?.length || record.inspectionSupplement.psAcceptingNewPatients?.length ?
      joinInsurances(record.psAcceptingNewPatients, record.inspectionSupplement.psAcceptingNewPatients).map((r, index) => `acceptingNewPatients-${index}`) :
      ["acceptingNewPatients"];
    const notAcceptingPatientsKeys = record.psNotAcceptingPatients?.length || record.inspectionSupplement.psNotAcceptingPatients?.length ?
      joinInsurances(record.psNotAcceptingPatients, record.inspectionSupplement.psNotAcceptingPatients).map((r, index) => `notAcceptingNewPatients-${index}`) :
      ["notAcceptingNewPatients"];
    const acceptingNewPatientsWithLimitationsKeys = record.psAcceptingNewPatientsWithLimitations?.length || record.inspectionSupplement.psAcceptingNewPatientsWithLimitations?.length ?
      joinInsurances(record.psAcceptingNewPatientsWithLimitations, record.inspectionSupplement.psAcceptingNewPatientsWithLimitations).map((r, index) => `acceptingPatientsWithLimitations-${index}`) :
      ["acceptingPatientsWithLimitations"];
    const acceptingExistingPatientsOnlyKeys = record.psAcceptingExistingPatientsOnly?.length || record.inspectionSupplement.psAcceptingExistingPatientsOnly?.length ?
      joinInsurances(record.psAcceptingExistingPatientsOnly, record.inspectionSupplement.psAcceptingExistingPatientsOnly).map((r, index) => `acceptingExistingPatientsOnly-${index}`) :
      ["acceptingExistingPatientsOnly"];
    const providerLanguagesKeys = record.providerLanguages?.length || record.inspectionSupplement.providerLanguages?.length ?
      joinProviderLanguages(record).map((l, index) => `providerLanguages-${index}`) :
      ["providerLanguages"];
    const locationLanguagesKeys = record.locationLanguages?.length || record.inspectionSupplement.locationLanguages?.length ?
      joinLocationLanguages(record).map((l, index) => `locationLanguages-${index}`) :
      ["locationLanguages"];
    const officeHoursKeys = record.officeHoursViewModels?.length ?
      record.officeHoursViewModels.map((oh, index) => `locationOfficeHours-${index}`) :
      ["locationOfficeHours"];
    const groupAffiliationKeys = record.groupAffiliation?.length || record.inspectionSupplement.groupAffiliation?.length ?
      joinGroupAffiliation(record).map((g, index) => `groupAffiliation-${index}`) :
      ["groupAffiliation"];
    const hospitalAffiliationKeys = record.hospitalAffiliation?.length || record.inspectionSupplement.hospitalAffiliation?.length ?
      joinHospitalAffiliation(record).map((g, index) => `hospitalAffiliation-${index}`) :
      ["hospitalAffiliation"];
    const ipaAffiliationKeys = record.ipaAffiliation?.length || record.inspectionSupplement.ipaAffiliation?.length ?
      joinIPAAffiliation(record).map((g, index) => `ipaAffiliation-${index}`) :
      ["ipaAffiliation"];
    const boardCertificationsKeys = record.inspectionSupplement.boardCertifications?.length ?
      record.inspectionSupplement.boardCertifications.map((g, index) => `boardCertifications-${index}`) :
      ["boardCertifications"];
    const medicaidLicensesKeys = record.inspectionSupplement.medicaidLicenses?.length ?
      record.inspectionSupplement.medicaidLicenses.map((g, index) => `medicaidLicenses-${index}`) :
      ["medicaidLicenses"];
    const raceKeys = record.inspectionSupplement.race?.length ?
      record.inspectionSupplement.race.map((g, index) => `race-${index}`) :
      ["race"];
    const ethnicityKeys = record.inspectionSupplement.ethnicity?.length ?
      record.inspectionSupplement.ethnicity.map((g, index) => `ethnicity-${index}`) :
      ["ethnicity"];
    const providerLicensesKeys = record.providerLicenses?.length || record.inspectionSupplement.providerLicenses?.length ?
      joinProviderLicenses(record).map((g, index) => `providerLicenses-${index}`) :
      ["providerLicenses"];
    const publicTransportationsKeys = record.inspectionSupplement.publicTransportations?.length ?
      record.inspectionSupplement.publicTransportations.map((g, index) => `publicTransportation-${index}`) :
      ["publicTransportation"];
    const culturalCompetencyTrainingsKeys = record.inspectionSupplement.culturalCompetencyTrainings?.length ?
      record.inspectionSupplement.culturalCompetencyTrainings.map((g, index) => `culturalCompetencyTraining-${index}`) :
      ["culturalCompetencyTraining"];
    const taxonomyKeys = record.inspectionSupplement.taxonomy?.length ?
      record.inspectionSupplement.taxonomy.map((g, index) => `taxonomy-${index}`) :
      ["taxonomy"];

    // first string is the category, array of strings is the row keys
    const tableRowGroups = new Map<string, string[]>([
      ["General", ["npi", "providerClaimsTotalVolume", "tin", "entityType", "locationName", "organizationNpi", "prefix", "firstName", "middleName", "lastName", "suffix", "locationPhone", "locationFax", "locationEmail", "specialtyCode", ...credentialsKeys, "gender", "additionalKnownSpecialties", "frequencyAtLocation"]],
      ["Address", ["address", "address2", "city", "state", "zip", "locationConfidenceScore"]],
      ["Insurance", [...acceptingNewPatientsKeys, ...notAcceptingPatientsKeys, ...acceptingNewPatientsWithLimitationsKeys, ...acceptingExistingPatientsOnlyKeys]],
      !this.props.enabledFeatureFlags.has(Features.QES_ACCURACY_ENABLE_LOCATION_WAIT_TIMES_PHASE_TWO)
        ? ["Miscellaneous", ["adaFeatures", ...providerLanguagesKeys, ...locationLanguagesKeys, "locationCertifiedSkilledMedicalInterpreter", "locationGenderAffirmingCare", "organizationWebsite", ...officeHoursKeys, ...groupAffiliationKeys, ...hospitalAffiliationKeys, ...ipaAffiliationKeys, ...boardCertificationsKeys, ...medicaidLicensesKeys, ...raceKeys, ...ethnicityKeys, ...providerLicensesKeys, "providerTelehealthServices", ...providerTelehealthTypeKeys, ...providerTelehealthOriginatingSiteKeys, "locationTelehealthServices", "locationAppointmentWaitTime", ...publicTransportationsKeys, ...culturalCompetencyTrainingsKeys, ...taxonomyKeys]]
        : ["Miscellaneous", ["adaFeatures", ...providerLanguagesKeys, ...locationLanguagesKeys, "locationCertifiedSkilledMedicalInterpreter", "locationGenderAffirmingCare", "organizationWebsite", ...officeHoursKeys, ...groupAffiliationKeys, ...hospitalAffiliationKeys, ...ipaAffiliationKeys, ...boardCertificationsKeys, ...medicaidLicensesKeys, ...raceKeys, ...ethnicityKeys, ...providerLicensesKeys, "providerTelehealthServices", ...providerTelehealthTypeKeys, ...providerTelehealthOriginatingSiteKeys, "locationTelehealthServices", ...publicTransportationsKeys, ...culturalCompetencyTrainingsKeys, ...taxonomyKeys]],
      !this.props.enabledFeatureFlags.has(Features.QES_ACCURACY_ENABLE_LOCATION_WAIT_TIMES_PHASE_TWO)
        ? ["", [""]]
        : ["Location Appointment Wait Times", ["preventiveCareAppointment", "routineDiagnosticCareAppointment", "urgentCareAppointment", "specialtyCareAppointment"]],
      ["Areas of Expertise", ["conditionsTreated", "proceduresAndTreatmentsPerformed", "populationsTreatedAge", "populationsTreatedGender", "populationsTreatedGroup"]],
    ]);

    return tableRowGroups;
  }

  private getAppointmentWaitTimesTableData = (category: AppointmentWaitTimeCategory, record: AccuracyProviderDetail, key: string) => {
    const appointmentWaitTime = record.inspectionSupplement.locationAppointmentWaitTimes.filter((r) => (r.value.category == category))
    const awtstring = appointmentWaitTime.length > 0 ? appointmentWaitTime[0].value : null;

    const output: InspectionProfileTableData = awtstring ?
      (
        {
          [wafflesKeyColumnName]: key,
          title: category,
          source: null,
          result: (
            <div>
              {getSupplementalDataDecoration(
                (<span>{valueOrDashes(getFriendlyAppointmentWaitTimeName(awtstring.value))}</span>),
                appointmentWaitTime.length > 0 ? appointmentWaitTime[0].supplementalType : null,
                null,
              )}
            </div>
          ),
          reference: (<>
            {/*Use the most recent source from multiple values*/
              valueOrDashes(appointmentWaitTime[appointmentWaitTime.length - 1].sourceName)} &nbsp;
            {(appointmentWaitTime[appointmentWaitTime.length - 1].sourceName) ?
              valueOrDashes(appointmentWaitTime[appointmentWaitTime.length - 1].sourceDate ?
                `(${formatUnconvertedDateCustom(appointmentWaitTime[appointmentWaitTime.length - 1].sourceDate, "L")})` : "") :
              null
            }
          </>)
        }
      ) :
      (
        generateDefaultProviderPanelRow(key, category, null, null)
      );

    return output;
  }

  private getAreaOfExpertiseTableData = (group: AreaOfExpertiseGroupEnum, record: AccuracyProviderDetail, key: string) => {
    const areasOfExpertise = record.inspectionSupplement.areasOfExpertise.filter((r) => (r.value.group == group))
    const aoeString = areasOfExpertise.map((aoe) => aoe.value.value).join('; ');

    const output: InspectionProfileTableData = aoeString?.length ?
      (
        {
          [wafflesKeyColumnName]: key,
          title: group,
          source: null,
          result: (
            <div>
              {getSupplementalDataDecoration(
                (<span>{valueOrDashes(aoeString)}</span>),
                areasOfExpertise.length > 0 ? areasOfExpertise[0].supplementalType : null,
                null,
              )}
            </div>
          ),
          reference: (<>
            {/*Use the most recent source from multiple values*/
              valueOrDashes(areasOfExpertise[areasOfExpertise.length - 1].sourceName)} &nbsp;
            {(areasOfExpertise[areasOfExpertise.length - 1].sourceName) ?
              valueOrDashes(areasOfExpertise[areasOfExpertise.length - 1].sourceDate ?
                `(${formatUnconvertedDateCustom(areasOfExpertise[areasOfExpertise.length - 1].sourceDate, "L")})` : "") :
              null
            }
          </>)
        }
      ) :
      (
        generateDefaultProviderPanelRow(key, group, null, null)
      );

    return output;
  }
}
/* eslint-enable */

function generateProviderLicenseName(providerLicense: ProviderLicense) {
  return `${valueOrDashes(providerLicense.licenseNumber)}:${providerLicense.licenseState}:${providerLicense.licenseType}:${providerLicense.issueDate ? formatUnconvertedDateCustom(providerLicense.issueDate, "L") : ""}:${providerLicense.expirationDate ? formatUnconvertedDateCustom(providerLicense.expirationDate, "L") : ""}:${providerLicense.active ? "ACTIVE" : "INACTIVE"}`;
}

function generateBoardCertificationName(boardCertification: BoardCertification) {
  return `American Board of ${boardCertification.memberBoardName}:${boardCertification.certificateName}:${boardCertification.startDate ? formatUnconvertedDateCustom(boardCertification.startDate, "L") : ""}:${boardCertification.endDate ? formatUnconvertedDateCustom(boardCertification.endDate, "L") : ""}:${boardCertification.certificationStatus}`;
}

function joinProviderLanguages(accuracyProviderDetail: AccuracyProviderDetail) {
  const langs = accuracyProviderDetail.providerLanguages.map((lang) => ({
    comparedValue: lang.toLowerCase(),
    name: lang
  }));
  const supps = accuracyProviderDetail.inspectionSupplement.providerLanguages.filter((obj) => obj.supplementalType != "unknown").map((lang) => ({
    comparedValue: lang.value.toLowerCase(),
    sourceDate: lang.sourceDate,
    sourceName: lang.sourceName,
    supplementalType: lang.supplementalType,
    value: lang.value
  }));
  const merged = merge(keyBy(langs, "comparedValue"), keyBy(supps, "comparedValue"));
  return values(merged);
}

function joinLocationLanguages(accuracyProviderDetail: AccuracyProviderDetail) {
  const langs = accuracyProviderDetail.locationLanguages.map((lang) => ({
    comparedValue: lang.toLowerCase(),
    name: lang
  }));
  const supps = accuracyProviderDetail.inspectionSupplement.locationLanguages.map((lang) => ({
    comparedValue: lang.value.toLowerCase(),
    sourceDate: lang.sourceDate,
    sourceName: lang.sourceName,
    supplementalType: lang.supplementalType,
    value: lang.value
  }));
  const merged = merge(keyBy(langs, "comparedValue"), keyBy(supps, "comparedValue"));
  return values(merged);
}

function normalizeAffiliationName(name: string) {
  const regex = /[^a-zA-Z0-9]/gm;
  return name.toLowerCase().replace(regex, '');
}

function joinGroupAffiliation(accuracyProviderDetail: AccuracyProviderDetail) {
  const affils = accuracyProviderDetail.groupAffiliation.filter((affil) => affil.name != "").map((affil) =>
  ({
    comparedValue: normalizeAffiliationName(affil.name),
    name: affil.name
  }));
  const supps = accuracyProviderDetail.inspectionSupplement.groupAffiliation.filter((obj) => obj.supplementalType != "unknown").map((affil) =>
  ({
    comparedValue: normalizeAffiliationName(affil.value.providerName ?? affil.value.name),
    sourceDate: affil.sourceDate,
    sourceName: affil.sourceName,
    supplementalType: affil.supplementalType,
    value: affil.value
  }));
  const merged = merge(keyBy(affils, "comparedValue"), keyBy(supps, "comparedValue"));
  const sorted = sortBy(merged, [((affil) => affil && affil.name ? affil.name : null), ((affil) => affil && affil.value ? affil.value : null)]);
  const r = values(sorted);
  return r;
}

function joinHospitalAffiliation(accuracyProviderDetail: AccuracyProviderDetail) {
  const affils = accuracyProviderDetail.hospitalAffiliation.filter((affil) => affil.name != "").map((affil) => ({
    admittingPrivilege: affil.admittingPrivilege,
    comparedValue: normalizeAffiliationName(affil.name),
    name: affil.name,
    type: affil.affiliationType
  }));
  const supps = accuracyProviderDetail.inspectionSupplement.hospitalAffiliation.filter((obj) => obj.supplementalType != "unknown").map((affil) => ({
    comparedValue: normalizeAffiliationName(affil.value.providerName ?? affil.value.name),
    sourceDate: affil.sourceDate,
    sourceName: affil.sourceName,
    supplementalType: affil.supplementalType,
    value: affil.value
  }));
  const merged = merge(keyBy(affils, "comparedValue"), keyBy(supps, "comparedValue"));
  const sorted = sortBy(merged, [((affil) => affil && affil.name ? affil.name : null), ((affil) => affil && affil.value && affil.value.name ? affil.value.name : null)]);
  const r = values(sorted);
  return r;
}

function joinIPAAffiliation(accuracyProviderDetail: AccuracyProviderDetail) {
  const affils = accuracyProviderDetail.ipaAffiliation.filter((affil) => affil.name != "").map((affil) =>
  ({
    comparedValue: normalizeAffiliationName(affil.name),
    name: affil.name
  }));
  const supps = accuracyProviderDetail.inspectionSupplement.ipaAffiliation.filter((obj) => obj.supplementalType != "unknown").map((affil) =>
  ({
    comparedValue: normalizeAffiliationName(affil.value.providerName ?? affil.value.name),
    sourceDate: affil.sourceDate,
    sourceName: affil.sourceName,
    supplementalType: affil.supplementalType,
    value: affil.value
  }));
  const merged = merge(keyBy(affils, "comparedValue"), keyBy(supps, "comparedValue"));
  const sorted = sortBy(merged, [((affil) => affil && affil.name ? affil.name : null), ((affil) => affil && affil.value ? affil.value : null)]);
  const r = values(sorted);
  return r;
}

function joinProviderLicenses(accuracyProviderDetail: AccuracyProviderDetail) {
  const licenses = accuracyProviderDetail?.providerLicenses?.map((lic) => ({ license: { name: generateProviderLicenseName(lic), value: lic } }));
  const supps = accuracyProviderDetail?.inspectionSupplement.providerLicenses.filter(p => p.value != null).map((supp) => ({ supplement: { name: generateProviderLicenseName(supp.value), value: supp } }));
  const merged = merge(keyBy(licenses, "license.name"), keyBy(supps, "supplement.name"));
  const sorted = sortBy(merged, [((lic) => lic && lic?.license?.name ? lic.license.name : null), ((supp) => supp && supp?.supplement?.name ? supp.supplement.name : null)]);
  const r = values(sorted);
  return r;
}

function joinPublicTransportation(publicTransportation: PublicTransportation) {
  return `${publicTransportation.type.replace(/_/g, " ")}: ${publicTransportation.distance ? publicTransportation.distance : ""}`;
}

function joinCulturalCompetencyTraining(culturalCompetencyTraining: CulturalCompetencyTraining) {
  const expires = culturalCompetencyTraining.expires ? formatUnconvertedDateCustom(culturalCompetencyTraining.expires, "L") : "Expiration Unknown";
  return `${culturalCompetencyTraining.state}: ${expires}`;
}

function joinMedicaidLicense(medicaidLicense: MedicaidLicense) {
  return `${medicaidLicense.state ? medicaidLicense.state : ""}:${medicaidLicense.number ? medicaidLicense.number : ""}`;
}

function formatZipCode(zipCode: string) {
  if (zipCode?.length === 9) {
    return zipCode.replace(/(\d{5})/, "$1-");
  } else {
    return zipCode;
  }
}

function getFriendlyAppointmentWaitTimeName(appointmentWaitTime: string) {
  switch (appointmentWaitTime) {
    case "within_10_business_days":
      return "Within 10 Business Days";
    case "within_15_business_days":
      return "Within 15 Business Days";
    case "within_30_business_days":
      return "Within 30 Business Days";
    case "over_30_business_days":
      return "Over 30 Business Days";
    case "greater_than_30_days":
      return "Greater than 30 Days";
    case "im_not_sure":
      return "I'm Not Sure";
    case "not_applicable":
      return "Not Applicable";
  }

  const regex = /^\d{1,2} Days$/;
  if (regex.test(appointmentWaitTime))
    return appointmentWaitTime;

  return null;
}

function getFriendlyYesNoUnknownLabel(interpreter: string) {
  switch (interpreter) {
    case "yes":
      return "Yes";
    case "no":
      return "No";
    case "i_dont_know":
      return "I Don't Know";
  }

  return null;
 }

const WrappedAccuracyProviderInspectionProfile = withUserContext(withCountyProjectContext(withFeatureFlagContext(AccuracyProviderInspectionProfile)));

export {
  WrappedAccuracyProviderInspectionProfile as AccuracyProviderInspectionProfile,
  InspectionProfileTableData,
  valueOrDashes,
  getSupplementalDataDecoration,
  getSupplementalDataDecorationWithoutSource
};
